/**
 * Quais colunas a planilha mostra e em que ordem.
 *
 * A largura de cada coluna já era guardada por navegador (`columnWidths`); ordem e visibilidade
 * seguem o mesmo caminho, e pela mesma razão: é preferência de quem opera, não padrão do
 * laboratório. Dois protéticos na mesma bancada olham colunas diferentes — quem monta quer material
 * e cor, quem faz o acabamento quer prazo e responsável.
 *
 * A consequência disso é que trocar de máquina perde o arranjo. Se algum dia o dono quiser
 * padronizar pra equipe, isso vira coluna no banco — não é o caso hoje.
 */
export const COLUMN_KEYS = [
  "select",
  "status",
  "modelo",
  "estagio",
  "data",
  "prazo",
  "servico",
  "responsavel",
  "clinica",
  "dentista",
  "paciente",
  "dentes",
  "material",
  "materiais",
  "cor",
  "obs",
  "anexo",
  "pedido",
  "finalizar",
] as const;

export type ColumnKey = (typeof COLUMN_KEYS)[number];

export const COLUMN_LABELS: Record<ColumnKey, string> = {
  select: "Seleção",
  status: "Status",
  modelo: "Modelo",
  estagio: "Estágio",
  data: "Data",
  prazo: "Prazo",
  servico: "Serviço",
  responsavel: "Responsável",
  clinica: "Clínica",
  dentista: "Dentista",
  paciente: "Paciente",
  dentes: "Dentes",
  material: "Material",
  materiais: "Insumos gastos",
  cor: "Cor",
  obs: "Observações",
  anexo: "Anexo",
  pedido: "Abrir pedido",
  finalizar: "Finalizar",
};

/**
 * Colunas que não são dado, são AÇÃO — e por isso não se movem nem se escondem.
 *
 * A caixa de seleção abre a linha e o "Finalizar" a fecha; arrastar qualquer uma delas pro meio da
 * tabela quebraria a leitura de todo mundo, e escondê-las tiraria da tela a única forma de concluir
 * um serviço. A de seleção também é a coluna grudada na esquerda no celular.
 */
export const COLUNAS_FIXAS: ColumnKey[] = ["select", "finalizar"];

const LS_ORDEM = "bookdental.producao.planilha.columnOrder";
const LS_OCULTAS = "bookdental.producao.planilha.hiddenColumns";

function lerLista(chave: string): string[] | null {
  if (typeof window === "undefined") return null;
  try {
    const bruto = window.localStorage.getItem(chave);
    if (!bruto) return null;
    const lido = JSON.parse(bruto);
    return Array.isArray(lido) && lido.every((v) => typeof v === "string") ? lido : null;
  } catch {
    return null;
  }
}

function gravarLista(chave: string, valor: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(chave, JSON.stringify(valor));
  } catch {
    // Cota estourada ou storage bloqueado: a planilha só perde a preferência, não quebra.
  }
}

/**
 * Ordem salva, reconciliada com o código.
 *
 * Nunca confia cegamente no que está no navegador: coluna que deixou de existir é descartada e
 * coluna nova entra na posição que ela tem no padrão. Sem isso, quem já usava a planilha ficaria
 * sem as colunas que criarmos depois — e o defeito apareceria só pra quem é usuário antigo, que é o
 * pior tipo de defeito.
 */
export function carregarOrdem(): ColumnKey[] {
  const salva = (lerLista(LS_ORDEM) ?? []).filter((k): k is ColumnKey =>
    (COLUMN_KEYS as readonly string[]).includes(k),
  );
  const faltando = COLUMN_KEYS.filter((k) => !salva.includes(k));
  const completa = [...salva];
  for (const k of faltando) completa.splice(COLUMN_KEYS.indexOf(k), 0, k);
  return fixarExtremos(completa);
}

export function carregarOcultas(): Set<ColumnKey> {
  const salvas = (lerLista(LS_OCULTAS) ?? []).filter((k): k is ColumnKey =>
    (COLUMN_KEYS as readonly string[]).includes(k),
  );
  // Coluna fixa nunca fica oculta, mesmo que alguém edite o localStorage à mão.
  return new Set(salvas.filter((k) => !COLUNAS_FIXAS.includes(k)));
}

export const salvarOrdem = (ordem: ColumnKey[]) => gravarLista(LS_ORDEM, fixarExtremos(ordem));
export const salvarOcultas = (ocultas: Set<ColumnKey>) =>
  gravarLista(LS_OCULTAS, [...ocultas].filter((k) => !COLUNAS_FIXAS.includes(k)));

/** Devolve a ordem com as colunas de ação de volta às pontas, aconteça o que acontecer. */
export function fixarExtremos(ordem: ColumnKey[]): ColumnKey[] {
  const meio = ordem.filter((k) => !COLUNAS_FIXAS.includes(k));
  return ["select", ...meio, "finalizar"];
}

/** As colunas que a tabela realmente desenha, na ordem certa. */
export function colunasVisiveis(ordem: ColumnKey[], ocultas: Set<ColumnKey>): ColumnKey[] {
  return ordem.filter((k) => !ocultas.has(k));
}

export const ehPadrao = (ordem: ColumnKey[], ocultas: Set<ColumnKey>) =>
  ocultas.size === 0 && ordem.join() === COLUMN_KEYS.join();
